// This file handles all API calls to the backend
// Every time we need data from the server, we use functions from this file

/**
 * NOTES-SUMMARY
 * 
 * What this file does:
 *  Creates axios instance with default settings
 *  Adds JWT token to all requests automatically
 *  Provides functions for every API call (login, getLeads, getInsights, etc.)
 *  Handles errors gracefully

 *How it works:
 *  Instead of writing axios.get(...) everywhere, we just call getLeads() and it handles everything.

 */



import axios from 'axios';
import { API_BASE_URL } from './constants';

// Create axios instance with default settings
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to all requests if it exists
// This authenticates the user
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Authentication APIs
 */

// Login function
// Sends email and password to backend
// Returns JWT token if successful
export const login = async (email, password) => {
  try {
    const response = await api.post('/login', { email, password });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Login failed');
  }
};

// Verify token function
// Checks if current JWT token is still valid
export const verifyToken = async () => {
  try {
    const response = await api.get('/verify');
    return response.data;
  } catch (error) {
    return null;
  }
};

/**
 * Dashboard APIs
 */

// Simple cache for getLeads to avoid redundant fetches on page navigation
let leadsCache = { data: null, timestamp: 0 };
let leadsInflight = null;
const LEADS_CACHE_TTL = 60000; // 60 seconds

// Get all leads
// Returns array of leads with scores and engagement
export const getLeads = async ({ bustCache = false } = {}) => {
  // Return cached data if fresh
  const now = Date.now();
  if (!bustCache && leadsCache.data && (now - leadsCache.timestamp) < LEADS_CACHE_TTL) {
    return leadsCache.data;
  }

  // Deduplicate concurrent requests
  if (leadsInflight) return leadsInflight;

  leadsInflight = (async () => {
    try {
      const response = await api.get('/get-leads');
      const data = response.data.data;
      leadsCache = { data, timestamp: Date.now() };
      return data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch leads');
    } finally {
      leadsInflight = null;
    }
  })();

  return leadsInflight;
};

// Get single lead with full details
// Returns lead with reasoning, next_action, and engagement
export const getLead = async (leadId) => {
  try {
    const response = await api.get(`/get-lead?id=${leadId}`);
    return response.data.data.lead;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch lead');
  }
};

// Get AI insights
// Returns daily insights and forecast
export const getInsights = async () => {
  try {
    const response = await api.get('/get-insights');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch insights');
  }
};

// Save AI insight
// Saves an insight for later viewing
export const saveInsight = async (text, originalDate) => {
  try {
    const response = await api.post('/save-insight', { text, originalDate });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to save insight');
  }
};

// Get saved insights
// Returns all saved insights
export const getSavedInsights = async () => {
  try {
    const response = await api.get('/get-saved-insights');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch saved insights');
  }
};

// Delete saved insight
// Deletes a saved insight by ID
export const deleteInsight = async (insightId) => {
  try {
    const response = await api.post('/delete-insight', { id: insightId });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete insight');
  }
};

// Get Henri stats
// Returns chatbot conversation metrics
export const getHenriStats = async () => {
  try {
    const response = await api.get('/get-henri-stats');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch Henri stats');
  }
};

// Get pipeline forecast
// Returns 30/60/90 day pipeline predictions
export const getForecast = async () => {
  try {
    const response = await api.get('/get-forecast');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch forecast');
  }
};

/**
 * AI APIs
 */

// Generate email template for a lead
// Sends lead email, returns personalized email
export const generateEmail = async (leadEmail) => {
  try {
    console.log('API: Calling email-template with:', { email: leadEmail });
    const response = await api.post('/email-template', { email: leadEmail });
    console.log('API: Got response:', response.data);
    return response.data.data;
  } catch (error) {
    console.error('API: Error response:', error.response?.data);
    console.error('API: Error status:', error.response?.status);
    throw new Error(error.response?.data?.error || 'Failed to generate email');
  }
};

// Save email template
// Saves an email template with name and content
export const saveEmailTemplate = async (templateName, templateText) => {
  try {
    const response = await api.post('/save-email-template', { templateName, templateText });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to save email template');
  }
};

// Get saved email templates
// Returns all saved email templates
export const getEmailTemplates = async () => {
  try {
    const response = await api.get('/get-email-templates');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch email templates');
  }
};

// Delete email template
// Deletes an email template by ID
export const deleteEmailTemplate = async (templateId) => {
  try {
    const response = await api.delete('/delete-email-template', { data: { templateId } });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete email template');
  }
};

/**
 * Sync APIs (for manual triggers)
 */

// Manually trigger HubSpot sync
export const syncHubSpot = async () => {
  try {
    const response = await api.post('/sync-hubspot');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Sync failed');
  }
};

// Manually trigger Instantly sync
export const syncInstantly = async () => {
  try {
    const response = await api.post('/sync-instantly-background');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Sync failed');
  }
};


// Manually trigger Calendly sync
export const syncCalendly = async () => {
  try {
    const response = await api.post('/sync-calendly');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Sync failed');
  }
};

/**
 * Campaign APIs
 */

// Get campaign statistics
// Returns cold email and nurture email campaign data
export const getCampaignStats = async () => {
  try {
    const response = await api.get('/get-campaign-stats');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch campaign stats');
  }
};

// Get SMS stats filtered by GHL tag (lightweight — skips Instantly/HubSpot)
export const getSMSStats = async (tag) => {
  try {
    const params = { smsOnly: 'true' };
    if (tag && tag !== 'all') params.smsTag = tag;
    const response = await api.get('/get-campaign-stats', { params });
    return response.data.data.smsStats;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch SMS stats');
  }
};

/**
 * GA4 Analytics APIs
 */

// Get GA4 website analytics data
// Returns sessions, page views, form submissions, etc.
export const getGA4Data = async () => {
  try {
    const response = await api.get('/get-ga4-data');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch GA4 data');
  }
};

/**
 * Newsletter APIs
 */

// Get newsletter subscribers count
// Returns subscriber count from HubSpot list
export const getNewsletterSubscribers = async () => {
  try {
    const response = await api.get('/get-newsletter-subscribers');
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to fetch newsletter subscribers');
  }
};

/**
 * Lead Management APIs
 */

// Update lead with new data
// Updates lead stage, assigned_to, notes, etc.
export const updateLead = async (leadId, updates) => {
  try {
    const response = await api.post('/update-lead', { leadId, updates });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to update lead');
  }
};

// Add note to lead
// Convenient wrapper for adding a single note
export const addLeadNote = async (leadId, noteText, createdBy = 'User') => {
  try {
    const updates = {
      notes: [{
        text: noteText,
        created_by: createdBy
      }]
    };
    const response = await updateLead(leadId, updates);
    return response;
  } catch (error) {
    throw new Error(error.message || 'Failed to add note');
  }
};

// Delete lead
// Removes lead and all related engagement data
export const deleteLead = async (leadId, email = null) => {
  try {
    const response = await api.post('/delete-lead', { leadId, email });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete lead');
  }
};